 <template>

    <div id="products">

        <div class="flex">
            <div class="flex-1 mb-4">
                <h1 class="text-2xl font-medium text-gray-900 capitalize">Product Synchronisation</h1>
            </div>

            <div class="flex mb-4 justify-end">
                <router-link :to="{ name: 'products' }" custom v-slot="{ navigate }">
                    <button @click="navigate" class="btn-outline justify-center px-4 ml-4">
                        Go back
                    </button>
                </router-link>

                <button type="button" class="btn justify-center px-4 ml-4" @click.prevent="showModal">
                    Start Product Synchronisation
                </button>
            </div>
        </div>

        <div class="flex flex-col" v-if="loaded">

            <div v-if="form.syncAvailable()">

                <div class="index-table pb-6">
                    <div>
                        <div>
                            <table>
                                <thead>
                                <tr>
                                    <th scope="col" class="text-left tracking-wider">
                                        Product Synchronisation
                                    </th>
                                    <th>Sync Progress</th>
                                    <th>&nbsp;</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr class="colored-row">
                                    <td>
                                        {{ form.sync.data.id }} -
                                        <span class="font-medium">{{ form.sync.data.processor }}</span>
                                        <span v-if="form.sync.data.processor_tenant_name">({{ form.sync.data.processor_tenant_name }})</span>
                                    </td>
                                    <td class="font-medium text-center">
                                        {{ form.sync.data.sync_done }} <span class="text-xl text-gray-700">/ {{ form.sync.data.sync_count }}</span>
                                    </td>
                                    <td class="text-right">
                                        {{ formatDate(form.sync.data.updated_at) }}<br>
                                        <span class="text-gray-400 text-xs">Started by {{ form.sync.data.user_name }}</span>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="index-table">
                    <div>
                        <div>
                            <table>
                                <thead>
                                <tr>
                                    <th scope="col" class="text-left tracking-wider hidden lg:table-cell">
                                        Image
                                    </th>
                                    <th scope="col" class="p-3 text-left tracking-wider">
                                        SKU
                                    </th>
                                    <th scope="col" class="p-3 text-left tracking-wider hidden lg:table-cell">
                                        Name / Tag
                                    </th>
                                    <th scope="col" class="p-3 text-left text-center hidden lg:table-cell">
                                        Price / Min - Max Load
                                    </th>
                                    <th scope="col" class="p-3">
                                        Synchronization Status
                                    </th>
                                    <th scope="col" class="relative p-3 text-right">
                                        <span class="sr-only">Synchronise Product</span>
                                    </th>
                                    <th>
                                        &nbsp;
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr class="colored-row" v-for="(item, index) in form.sync.data.items">
                                    <td class="hidden lg:table-cell items-center text-center">
                                        <img :src="item.data.image_url" alt="Missing Image" class="w-32 h-20 mx-auto rounded shadow-md">
                                    </td>
                                    <td>
                                        <h3 class="sub-heading font-medium text-brand-700">{{ ellipsis(item.data.sku, 20) }}</h3>
                                        <br>
                                        <span class="status-box status-pending mr-1">
                                            {{ item.data.discount ? item.data.discount / 100 : 0 }}% Discount
                                        </span>
                                        <span class="status-box status-complete" v-if="item.data.active">Active</span>
                                        <span class="status-box status-cancelled" v-else>Inactive</span>

                                    </td>
                                    <td class="hidden lg:table-cell">
                                        <span class="font-medium text-error-500" v-if="!item.data.brand || item.data.brand === '-'">
                                            No Brand Set
                                        </span>
                                        <span class="font-medium" v-else>{{ item.data.brand }}</span>
                                        - {{ ellipsis(item.data.name, 20) }}
                                        <br>
                                        <ul class="grid grid-cols-2 xl:grid-cols-3 gap-2 mt-2" v-if="item.data.tags.length > 0">
                                            <li class="px-1 rounded text-center rounded border-2 text-xs status-open" v-for="tag in item.data.tags">
                                                {{ tag.name }}
                                            </li>
                                        </ul>
                                        <span class="text-gray-400" v-else>
                                            No Tags
                                        </span>
                                    </td>
                                    <td class="hidden lg:table-cell text-center">
                                        {{ formatCurrency(item.data.currency, item.data.price / 100) }}<br>
                                        {{ formatCurrency(item.data.currency, item.data.minimum_load / 100) }} - {{ formatCurrency(item.data.currency, item.data.maximum_load / 100) }}
                                    </td>
                                    <td class="text-center">
                                        <span class="text-xs font-gray-400">Synchronized on</span><br>
                                        <div class="flex justify-center">
                                            <span class="font-medium" v-if="item.last_sync_date">
                                                {{ formatDate(item.last_sync_date) }}
                                            </span>
                                            <span class="text-error-500 text-xs" v-else>Not Synchronized</span>

                                            <ConfirmationModal button_classes="btn-error text-sm justify-center px-6" button_text="Delete Account" @confirmModal="deleteProduct(item.sku)" v-if="item.last_sync_date">
                                                <template v-slot:clickable="{ toggleModal }">
                                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 cursor-pointer ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" @click="toggleModal">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </template>
                                                <div class="mt-3 text-center sm:mt-5">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                        Remove Product Access?
                                                    </h3>
                                                    <h4 class="text-lg leading-6 font-medium text-gray-400 mt-2">
                                                        {{ item.data.name }}
                                                    </h4>
                                                    <div class="mt-2">
                                                        <p class="text-sm text-gray-500">
                                                            Are you sure you would like to delete this product? Deleting this
                                                            product will disable access to this product across the system.
                                                        </p>
                                                    </div>
                                                </div>
                                            </ConfirmationModal>
                                        </div>
                                    </td>
                                    <td class="text-right text-xs">
                                        <button type="button" class="btn-outline text-xs" @click.prevent="form.confirmProduct(item.id)" v-if="!item.confirmed || !item.last_sync_date">Synchronise</button>
                                        <span v-else>
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-brand-600 mx-auto" viewBox="0 0 20 20" fill="currentColor">
                                              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                            </svg>
                                        </span>
                                    </td>
                                    <td>

                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div>
            <div class="panel" v-else>
                <div class="p-10">
                    <div class="text-gray-200 my-6">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                    </div>

                    <h2 class="text-gray-600 font-medium text-2xl text-center">Start Product Synchronisation</h2>

                    <p class="text-center mt-4 text-sm text-gray-500">
                        <button type="button" class="btn justify-center px-4 ml-4" @click.prevent="startProductSynchronisation()">
                            Start Product Synchronisation
                        </button>
                    </p>
                </div>
            </div>

            <transition
                enter-active-class="ease-out duration-300"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="ease-in duration-200"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <div class="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" v-show="display">
                    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" @click="closeModal"></div>

                        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <form method="post" @submit.prevent="startProductSynchronisation()" class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div>
                                <h3>Start Product Sync</h3>

                                <div class="form-control col-span-6 xl:col-span-4">
                                    <label for="product_sync_processor" class="block text-sm font-medium text-gray-700">Driver<i></i></label>
                                    <select name="processor" id="product_sync_processor" v-model="form.processor" required>
                                        <option :value="null">Select Processor</option>
                                        <option :value="processor.id" v-for="processor in form.processors.data">
                                            {{ processor.id }} ({{ processor.driver }} {{ processor.tenant_name ? ' | ' + processor.tenant_name : '' }})
                                        </option>
                                    </select>
                                </div>

                            </div>
                            <div class="mt-5">
                                <button type="submit" class="btn w-full justify-center">
                                    Start Product Sync
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </transition>

        </div>
        <Loading message="Loading Product Synchronisation" v-else></Loading>

    </div>

</template>

<script>
import Loading from "../Support/Loading";
import {ProductSync} from "../../app/Product/ProductSync";
import {Product} from "../../app/Product/Product";
import ConfirmationModal from "../Support/ConfirmationModal";
import {FormattingMixin, PermissionMixin} from "../../support/mixins";

export default {
    name: "ProductSync",
    mixins: [PermissionMixin, FormattingMixin],
    data() {
        return {
            display: false,
            form: null,
            loaded: false,
        }
    },
    components: {
        ConfirmationModal,
        Loading
    },
    methods: {
        deleteProduct(sku) {
            let vm = this;
            let product = new Product();

            vm.display = false;

            product.deleteProduct(sku).then(response => {
                vm.form.index().then(result => {
                    vm.display = false;
                });
            });
        },
        showModal() {
            this.form.resetFormMessages().then(result => {
                this.display = true;
            });
        },
        closeModal() {
            this.display = false;
        },
        startProductSynchronisation() {
            let vm = this;

            vm.form.startSync().then(result => {
                vm.display = false;
            });
        }
    },
    mounted() {
        let vm = this;

        new Promise(resolve => {
            vm.form = new ProductSync();

            vm.form.index().then(response => {
                resolve(true);
            }).catch(error => vm.handleError(error));

        }).then(result => {
            vm.loaded = result;
        });
    }
}
</script>


